import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { CareIcon } from "@/components/icons/CareIcon";
import { AnalysisIcon } from "@/components/icons/AnalysisIcon";
import { ResultsIcon } from "@/components/icons/ResultsIcon";
import { CheckmarkIcon } from "@/components/icons/CheckmarkIcon";
import { binIcon } from "@/assets/images";
import editIcon from "@/assets/images/icons/edit.svg";
import { formatPrice, Product } from "@/components/product-step/types";
import { voucherDiscountCents } from "@/utils/voucherDiscount";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { marketHasMembership } from "@/config/market";
import { usePrepayEnabled } from "@/hooks/usePrepayEnabled";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { useClinics } from "@/hooks/useClinics";
import { resolveProductKey, resolveClinicName } from "@/locales";
import { languageMap } from "@/i18n";
import { useBookingStore } from "@/stores/bookingStore";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useMembership } from "@/contexts/MembershipContext";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useMembershipTiers } from "@/hooks/useMembershipTiers";
import {
  getCadencePriceChf,
  CADENCE_CONTENTS_KEY,
} from "@/utils/membershipPricing";
import { usePaymentStepCta } from "./usePaymentStepCta";
import { PaymentCtaButton } from "./PaymentCtaButton";
import {
  computeBloodMembershipPricing,
  memberContributionCents,
  isMembershipEligible,
  isBloodMembershipBaseProduct,
  isScanPackage,
  bundleSavingsCents,
} from "@/utils/bloodMembershipPricing";
import { useBundleSummary } from "@/hooks/useBundleSummary";
import { CadencePickerModal } from "@/components/booking-form/CadencePickerModal";
import { trackMriScanToggle } from "@/utils/analytics";

interface PersistentDesktopSidebarProps {
  isVisible: boolean;
}

// Skeleton loader for the sidebar content
const SidebarSkeleton = () => (
  <div className="animate-pulse">
    <div className="space-y-4">
      <div className="bg-gray-100 rounded-2xl p-5">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
      <div className="bg-gray-100 rounded-2xl p-5">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const PersistentDesktopSidebar: React.FC<
  PersistentDesktopSidebarProps
> = ({ isVisible }) => {
  const [cadenceModalOpen, setCadenceModalOpen] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const wasVisibleRef = useRef(false);

  const {
    language,
    currentStep,
    selectedProducts,
    selectedAddOns,
    selectedProductDetails,
    selectedAddOnDetails,
    selectedClinic: selectedClinicId,
    selectedTimeSlots,
    goToStep,
    goBack,
    canGoBack,
    canProceed,
    toggleAddOn,
    removeProduct,
    isSubmitting,
    isSubmitted,
    selectedPackageType,
  } = useBookingFlow();

  const { market } = useMarket();
  const prepayEnabled = usePrepayEnabled();
  const { data: flags } = useFeatureFlags();
  const membershipFeatureOn =
    marketHasMembership(market) && (flags?.["membership_v2"] ?? true);

  const { isMember, isEntitledProduct, getEffectivePrice, membership } =
    useMembership();
  const selectedPaymentMode = useBookingFlowStore((s) => s.selectedPaymentMode);
  const offerMembership =
    membershipFeatureOn &&
    isMembershipEligible([...selectedProductDetails, ...selectedAddOnDetails]);
  // isYearlyBlood is the BLOOD-ONLY path-B branch. A scan cart also offers path B
  // but routes through isBundleMode (below), so it's excluded here via
  // !cartHasScan — keeping the two distinct so a scan is never priced as
  // standalone-blood member pricing while checkout charges the netted bundle.
  const cartHasScan = selectedProductDetails.some(isScanPackage);
  const isYearlyBlood =
    !isMember &&
    offerMembership &&
    !cartHasScan &&
    selectedPaymentMode === "yearly";

  // The details-step CTA (membership/Stripe, pick-option, or relay submit) — one
  // shared decision, rendered below via <PaymentCtaButton>.
  const paymentCta = usePaymentStepCta(language);

  const { data: clinics } = useClinics();
  const { t: i18n } = useTranslation(undefined, { lng: languageMap[language] });
  const discount = useBookingStore((state) => state.discount);

  const selectedClinic = clinics?.find((c) => c.id === selectedClinicId);

  // Membership cadence: when active, the order summary shows a single "Ahead
  // membership" line priced at the selected cadence instead of the product. The
  // price comes from the real membership tiers (Stripe source of truth); we
  // synthesize a product line for it and it reacts to plan changes.
  const planActiveRaw = useMembershipPlanStore((s) => s.active);
  const membershipPlanActive = membershipFeatureOn && planActiveRaw;
  const membershipPlan = useMembershipPlanStore((s) => s.plan);
  // Membership-first: the user started from the membership, so THAT is the
  // starting point the pencil should return to — and the scan, picked later,
  // gets a bin that simply drops it. MRI-first keeps the opposite arrangement,
  // where the package is where the booking began.
  const membershipFirstRaw = useMembershipPlanStore((s) => s.membershipFirst);
  const membershipFirst = membershipFeatureOn && membershipFirstRaw;
  const membershipTiers = useMembershipTiers().tiers;
  // Bundle view (is-bundle, cadence price, netted pricing, per-line scan override)
  // — derived once and shared with the mobile summary + add-ons step. See
  // useBundleSummary.
  const { isBundleMode, cadenceCents, bundlePricing, scanOverride } =
    useBundleSummary(selectedProductDetails, selectedAddOnDetails);

  // Bundle: what membership actually saves on this cart, right now. Measured
  // against the till (see bundleSavingsCents), so a cart the membership makes
  // more expensive shows nothing rather than a made-up figure.
  const memberSavingsCents =
    isBundleMode && bundlePricing
      ? bundleSavingsCents(
          [...selectedProductDetails, ...selectedAddOnDetails],
          bundlePricing.payNowCents,
          membershipPlan,
        )
      : 0;

  // Detach the membership without dropping the scan (scan reverts to its normal
  // non-member price). Store action so the removal persists to the session — see
  // bookingFlowStore.detachMembership. Wired to the remove control on the recurring
  // line and the synthesized membership line.
  const detachMembership = useBookingFlowStore((s) => s.detachMembership);
  // Drops the scan and keeps the membership — the bin on a membership-first
  // bundle means "not this scan", never "cancel the whole booking".
  const detachScanFromMembership = useBookingFlowStore(
    (s) => s.detachScanFromMembership,
  );
  const membershipProduct: Product = {
    id: "membership-plan",
    // Resolved via resolveProductKey in the order-summary renderer.
    name: "membershipCadence.orderLineName",
    description: null,
    images: [],
    default_price: {
      id: `membership-${membershipPlan}`,
      unit_amount: cadenceCents,
      currency: "chf",
    },
    product_type: "package",
  };
  // Show the "Ahead membership" line whenever the order is a membership: the
  // membership flow (path A), OR the blood-test upsell (path B) once the user
  // picks "become a member" — there the standalone blood test IS the membership,
  // so it's replaced by the membership line (add-ons stay as one-time items).
  // Bundle mode keeps the real scan in effectiveProductDetails (membership shown
  // in a separate recurring section below).
  const hasStandaloneBlood = selectedProductDetails.some(
    isBloodMembershipBaseProduct,
  );
  const showMembership =
    membershipPlanActive ||
    (isYearlyBlood && currentStep === "details" && hasStandaloneBlood);
  const effectiveProductDetails =
    showMembership && !isBundleMode
      ? [membershipProduct]
      : selectedProductDetails;
  // Members can stack add-ons (extra tests) on top of the plan; they're priced
  // at the member rate alongside the membership line.
  const effectiveAddOnDetails = selectedAddOnDetails;

  // Calculate prices (membership-aware)
  const allProductDetails = [
    ...effectiveProductDetails,
    ...effectiveAddOnDetails,
  ];

  // Original total before any membership discount (for showing savings)
  const originalTotal = allProductDetails.reduce(
    (sum, product) => sum + (product.default_price?.unit_amount || 0),
    0,
  );

  // Member pricing applies in two cases:
  //  • Membership path (path A): the plan IS the membership, so member rates
  //    apply throughout the flow (cadence, add-ons, details).
  //  • Blood upsell path (path B): a non-member who chose "pay now" gets member
  //    pricing, but only on the details step where the payment choice is made.
  //  • Bundle mode: add-ons are member-priced alongside the scan.
  const bloodPricing = computeBloodMembershipPricing(allProductDetails);
  const showMemberPricing =
    showMembership ||
    isBundleMode ||
    (!isMember &&
      isYearlyBlood &&
      currentStep === "details" &&
      bloodPricing.savingsCents > 0);

  const basePrice = showMemberPricing
    ? bloodPricing.memberTotalCents
    : allProductDetails.reduce(
        (sum, product) => sum + getEffectivePrice(product),
        0,
      );

  // basePrice is whole-CHF (floored in getEffectivePrice), so arithmetic is exact
  const membershipSavings = isMember
    ? originalTotal - basePrice
    : showMemberPricing
      ? bloodPricing.savingsCents
      : 0;

  // The voucher applies to what is PAID NOW. In bundle mode that is the bundle
  // pay-now total (netted scan + add-ons + first cadence invoice) — the old
  // bundle branch never subtracted the voucher at all, so the sidebar showed
  // "Subtotal 3'409 / code −150 / Total 3'409" while Stripe charged 3'259.
  const preDiscountTotal =
    isBundleMode && bundlePricing ? bundlePricing.payNowCents : basePrice;
  // Membership carts pass the individual invoice lines (bundle lines, or the
  // blood model's member lines): Stripe rounds a percentage coupon per line,
  // so a total computed on the sum would not be the total the customer is
  // charged. The pay-later path keeps the aggregate — that quote is recorded,
  // not invoiced line by line.
  const discountCents = voucherDiscountCents(
    discount,
    isBundleMode && bundlePricing
      ? bundlePricing.lines
      : showMemberPricing
        ? bloodPricing.memberLines
        : preDiscountTotal,
  );
  const totalPrice = preDiscountTotal - discountCents;

  // Handle the loading/fade transition when sidebar becomes visible
  useEffect(() => {
    if (isVisible && !wasVisibleRef.current) {
      // Sidebar just became visible - show skeleton first
      setIsContentReady(false);
      setShowContent(false);

      // After a short delay, mark content as ready and start fade-in
      const timer = setTimeout(() => {
        setIsContentReady(true);
        // Small delay before fading in content
        requestAnimationFrame(() => {
          setShowContent(true);
        });
      }, 320); // Wait for slide animation to be mostly complete

      return () => clearTimeout(timer);
    } else if (!isVisible) {
      // Reset when hidden
      setIsContentReady(false);
      setShowContent(false);
    }

    wasVisibleRef.current = isVisible;
  }, [isVisible]);

  const translations = {
    orderSummary: i18n("orderSummary.title"),
    scan: i18n("orderSummary.scan"),
    addOns: i18n("orderSummary.addOns"),
    bloodTests: i18n("orderSummary.bloodTests"),
    location: i18n("orderSummary.location"),
    timeSlots: i18n("orderSummary.timeSlots"),
    totalPrice: i18n("orderSummary.totalPrice"),
    continue: i18n("common.next"),
    back: i18n("common.prev"),
  };

  const handleContinue = () => {
    if (currentStep === "cadence") {
      // Membership flow: cadence → add-ons (scans live on the add-ons page).
      goToStep("addons");
    } else if (currentStep === "addons") {
      goToStep("clinic");
    } else if (currentStep === "clinic") {
      goToStep("date");
    } else if (currentStep === "date" || currentStep === "time") {
      goToStep("details");
    }
  };

  // Which control a cart line carries, and what it does. One-line sections
  // hoist the control into their header row (next to "One-off payment"), so
  // the decision has to be computable outside the line map.
  const productLineControls = (
    product: Product,
  ): { onEdit?: () => void; onRemove?: (id: string) => void } => {
    const isPackage = product.product_type === "package";
    const isMandatory =
      product.id === "prod_RMmfeLnfXxMKdO" || product.mandatory === true;
    const isProductAddOn = selectedAddOnDetails.some(
      (a) => a.id === product.id,
    );
    // The scan inside a membership-first bundle is the thing that was added,
    // not the thing the booking started from — bin, not pencil.
    const isBundledScanLine =
      membershipFirst && isBundleMode && isPackage && !isProductAddOn;
    const shouldShowEditIcon =
      (isPackage || isMandatory) &&
      !isProductAddOn &&
      currentStep !== "product" &&
      !isBundledScanLine;
    const shouldShowRemoveIcon =
      (!isPackage && !isMandatory) || isProductAddOn || isBundledScanLine;
    return {
      onEdit: shouldShowEditIcon
        ? () => goToStep("product", { skipValidation: true })
        : undefined,
      onRemove: shouldShowRemoveIcon
        ? (id: string) =>
            isProductAddOn
              ? toggleAddOn(id)
              : product.id === "membership-plan"
                ? detachMembership()
                : isBundledScanLine
                  ? (trackMriScanToggle(
                      false,
                      product.name,
                      product.default_price?.unit_amount ?? null,
                      "cart",
                    ),
                    detachScanFromMembership())
                  : removeProduct(id)
        : undefined,
    };
  };

  const renderProductItem = (
    product: Product,
    onEdit?: () => void,
    onRemove?: (id: string) => void,
    overrideDiscountedCents?: number,
  ) => (
    // first:pt-0 rather than a flat pb-2: the py-2 is what separates STACKED
    // product rows from each other, so it has to stay between them. Only the
    // first row's top padding is wrong — it lands under a section header that
    // already carries mb-2 and doubles the gap.
    <div key={product.id} className="py-2 first:pt-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {product.id === "membership-plan" ? (
            <div
              className="w-12 h-12 flex-shrink-0 rounded-xl overflow-hidden flex items-center justify-center"
              style={{
                backgroundImage: "url(/membership-logo-bg.webp)",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <svg
                width="26"
                height="28"
                viewBox="0 0 15 16"
                fill="none"
                aria-hidden
              >
                <path
                  d="M11.3742 0H0V1.99316L11.2938 1.71601C12.3158 1.69133 13.1522 2.51707 13.1311 3.53073L12.8861 15.7934H14.8593V3.4586C14.8593 1.54897 13.2976 0 11.3723 0Z"
                  fill="white"
                />
                <path
                  d="M9.37226 4.30513L0 14.3525L1.42584 15.8028L10.8517 5.66047C11.2325 5.25045 11.2019 4.61264 10.7828 4.24059C10.3732 3.87612 9.74546 3.9046 9.37226 4.30513Z"
                  fill="white"
                />
              </svg>
            </div>
          ) : (
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden">
              {product.images?.[0] ? (
                <img
                  src={product.images[0]}
                  alt={resolveProductKey(language, product.name)}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 bg-gray-400 rounded opacity-60"></div>
              )}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-neutral-900 truncate">
              {resolveProductKey(language, product.name)}
            </p>
            {(() => {
              const originalAmount = product.default_price?.unit_amount || 0;
              const isEntitled = isEntitledProduct(product.stripe_product_id);
              const discountedAmount =
                overrideDiscountedCents !== undefined
                  ? overrideDiscountedCents
                  : isMember
                    ? getEffectivePrice(product)
                    : showMemberPricing
                      ? memberContributionCents(product)
                      : originalAmount;
              const hasDiscount =
                (isMember || showMemberPricing) &&
                discountedAmount !== originalAmount;

              return hasDiscount ? (
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span className="text-sm text-gray-400 line-through">
                    {formatPrice(originalAmount, market)}
                  </span>
                  <span className="text-sm text-neutral-900 font-medium">
                    {formatPrice(discountedAmount, market)}
                  </span>
                  {isEntitled && (
                    <span className="text-xs text-deep-green-600 font-medium">
                      {i18n("membership.included")}
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-sm text-neutral-900 font-medium">
                  {formatPrice(originalAmount, market)}
                </p>
              );
            })()}
          </div>
        </div>
        {(onEdit || onRemove) && (
          <div className="flex-shrink-0 ml-2">
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200"
              >
                <img src={editIcon} alt="Edit" className="h-5 w-5" />
              </button>
            )}
            {onRemove && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(product.id);
                }}
                className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200"
              >
                <img src={binIcon} alt="Remove" className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
      {/* Membership line: cadence contents on its own full-width row so the
          label doesn't wrap in the narrow middle column. Aligned under the name
          (clears the 48px icon + 12px gap). */}
      {product.id === "membership-plan" && (
        <p className="text-xs text-neutral-500 mt-1 pl-[60px]">
          {i18n(CADENCE_CONTENTS_KEY[membershipPlan])}
        </p>
      )}
    </div>
  );

  const renderClinicInfo = () => {
    if (!selectedClinic) return null;
    return (
      // pb-2, not py-2: the section header above already carries mb-2, so a
      // top padding here stacked into a 16px gap between "Location" and the
      // clinic photo — twice the space of any other section.
      <div className="flex items-center justify-between pb-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
            {selectedClinic.photo_url ? (
              <img
                src={selectedClinic.photo_url}
                alt={resolveClinicName(language, selectedClinic)}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <div className="w-6 h-6 bg-gray-400 rounded"></div>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900">
              {resolveClinicName(language, selectedClinic)}
            </p>
            <p className="text-sm text-gray-600">
              {selectedClinic.address_short || selectedClinic.address}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderTimeSlots = () => {
    if (!selectedTimeSlots || selectedTimeSlots.length === 0) return null;

    const formatDate = (date: Date) => {
      const locale = language;
      return date.toLocaleDateString(locale, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    return (
      <div className="space-y-3">
        {selectedTimeSlots.map((slot, index) => {
          const slotLabel =
            index === 0
              ? i18n("orderSummary.preferred")
              : i18n("orderSummary.alternative");

          return (
            <div key={`${slot.date.toDateString()}-${slot.time}`}>
              <p className="text-xs text-gray-500 mb-1">{slotLabel}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">
                      {formatDate(slot.date)}
                    </p>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{slot.time}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const allProducts = [...effectiveProductDetails, ...effectiveAddOnDetails];

  // Group products by category
  const productsByCategory = new Map<
    string,
    { products: Product[]; isAddOn: boolean }
  >();

  allProducts.forEach((product) => {
    let categoryName: string;
    let isAddOn = false;

    if (selectedAddOnDetails.some((a) => a.id === product.id)) {
      categoryName = translations.addOns;
      isAddOn = true;
    } else if (product.category?.name) {
      categoryName = resolveProductKey(language, product.category.name);
    } else {
      const isBloodTest =
        product.name.toLowerCase().includes("blood") ||
        (product.blood_values && Object.keys(product.blood_values).length > 0);
      const isKnownPackage =
        product.name.toLowerCase().includes("ahead core") ||
        product.name.toLowerCase().includes("package") ||
        product.product_type === "package";

      if (isKnownPackage) {
        categoryName = i18n("orderSummary.packages");
      } else {
        categoryName = isBloodTest
          ? translations.bloodTests
          : translations.scan;
      }
    }

    if (!productsByCategory.has(categoryName)) {
      productsByCategory.set(categoryName, { products: [], isAddOn });
    }
    productsByCategory.get(categoryName)!.products.push(product);
  });

  const sortedCategories = Array.from(productsByCategory.entries()).sort(
    ([, { isAddOn: isAddOnA }], [, { isAddOn: isAddOnB }]) => {
      if (isAddOnA && !isAddOnB) return 1;
      if (!isAddOnA && isAddOnB) return -1;
      return 0;
    },
  );

  return (
    <div className="hidden xl:block">
      <div
        className={`fixed top-0 right-0 h-screen w-1/4 bg-white border-l border-gray-200 shadow-sidebar-left flex flex-col z-50 transition-transform duration-[400ms] ${
          isVisible ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" }}
      >
        {/* Header */}
        <div className="px-5 pt-6 pb-6">
          <h2 className="text-xl text-gray-900 font-medium">
            {i18n("orderSummary.bookingSummary")}
          </h2>
          {allProducts.length === 0 && (
            <p className="text-sm text-gray-700 italic mt-2">
              {i18n("orderSummary.notSelectedYet")}
            </p>
          )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-5 pb-4 relative">
          {/* Skeleton loader - fades out when content is ready */}
          <div
            className={`absolute inset-0 px-5 pb-4 transition-opacity duration-[400ms] ${
              showContent ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
          >
            <SidebarSkeleton />
          </div>

          {/* Actual content - fades in when ready */}
          <div
            className={`transition-opacity duration-[400ms] ${
              showContent ? "opacity-100" : "opacity-0"
            }`}
          >
            {/* flex + gap rather than space-y: the membership line reorders to
                the top on the membership-first path, and space-y puts its
                margins in DOM order, not visual order. */}
            <div className="flex flex-col gap-4">
              {allProducts.length === 0 ? (
                <div className="border-2 border-gray-200 rounded-2xl p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-gray-500">
                      <CareIcon />
                      <p className="text-sm">
                        {i18n("orderSummary.careHighLevel")}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-gray-500">
                      <AnalysisIcon />
                      <p className="text-sm">
                        {i18n("orderSummary.inDepthAnalysis")}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-gray-500">
                      <ResultsIcon />
                      <p className="text-sm">
                        {i18n("orderSummary.resultsInDays")}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                sortedCategories.map(
                  ([categoryName, { products, isAddOn }]) => (
                    <div
                      key={categoryName}
                      className="bg-gray-100 rounded-2xl px-5 py-3"
                    >
                      {/* Category label. In the membership flow (standalone blood) we relabel the
                          groups by billing cadence. Bundle mode keeps natural names. */}
                      {
                        <div className="mb-2 flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-900">
                            {showMembership && !isBundleMode
                              ? isAddOn
                                ? i18n("orderSummary.oneTimeAddOns")
                                : i18n("orderSummary.recurringAnnually")
                              : isBundleMode && !isAddOn
                                ? i18n("orderSummary.oneOffPayment")
                                : categoryName}
                          </h4>
                          {isAddOn && currentStep !== "addons" && (
                            <button
                              onClick={() =>
                                goToStep("addons", { skipValidation: true })
                              }
                              className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200"
                            >
                              <img
                                src={editIcon}
                                alt="Edit"
                                className="h-4 w-4"
                              />
                            </button>
                          )}
                          {/* One-line section: its control lives up here, level
                              with the label (the Recurring card set the
                              pattern). Add-on sections keep per-line bins —
                              several lines, several targets. */}
                          {!isAddOn &&
                            products.length === 1 &&
                            (() => {
                              const { onEdit, onRemove } = productLineControls(
                                products[0],
                              );
                              if (onEdit)
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEdit();
                                    }}
                                    className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200"
                                  >
                                    <img
                                      src={editIcon}
                                      alt="Edit"
                                      className="h-4 w-4"
                                    />
                                  </button>
                                );
                              if (onRemove)
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onRemove(products[0].id);
                                    }}
                                    className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 transition-colors rounded-md hover:bg-gray-200"
                                  >
                                    <img
                                      src={binIcon}
                                      alt="Remove"
                                      className="h-3.5 w-3.5"
                                    />
                                  </button>
                                );
                              return null;
                            })()}
                        </div>
                      }
                      <div className="space-y-1">
                        {products.map((product) => {
                          const { onEdit, onRemove } =
                            productLineControls(product);
                          // A one-line section already showed this control in
                          // its header row — don't repeat it on the line.
                          const inHeader = !isAddOn && products.length === 1;
                          // Bundle mode: scan package rendered at netted member price.
                          return renderProductItem(
                            product,
                            inHeader ? undefined : onEdit,
                            inHeader ? undefined : onRemove,
                            scanOverride(
                              product,
                              selectedAddOnDetails.some(
                                (a) => a.id === product.id,
                              ),
                            ),
                          );
                        })}
                      </div>
                    </div>
                  ),
                )
              )}
              {/* Bundle: the recurring membership line. Membership-first puts it
                  first — it's what the booking started from — and gives it the
                  pencil back to that choice; the scan below then carries the
                  bin. MRI-first keeps it under the one-time items with a bin,
                  because there the package is the starting point. */}
              {isBundleMode && bundlePricing && allProducts.length > 0 && (
                <div
                  className={`bg-gray-100 rounded-2xl px-5 py-3 ${
                    membershipFirst ? "order-first" : ""
                  }`}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">
                      {i18n("orderSummary.recurringHeading")}
                    </h4>
                    {membershipFirst ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          goToStep("product", { skipValidation: true });
                        }}
                        className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200"
                      >
                        <img src={editIcon} alt="Edit" className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          detachMembership();
                        }}
                        className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 transition-colors rounded-md hover:bg-gray-200"
                        aria-label={i18n("membershipUpsell.remove")}
                      >
                        <img
                          src={binIcon}
                          alt={i18n("membershipUpsell.remove")}
                          className="h-3.5 w-3.5"
                        />
                      </button>
                    )}
                  </div>
                  {/* Same anatomy as the standalone membership line: logo
                      tile, name + sub-line, price. */}
                  <div className="flex items-center gap-3 py-1">
                    <div
                      className="w-12 h-12 flex-shrink-0 rounded-xl overflow-hidden flex items-center justify-center"
                      style={{
                        backgroundImage: "url(/membership-logo-bg.webp)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      <svg
                        width="26"
                        height="28"
                        viewBox="0 0 15 16"
                        fill="none"
                        aria-hidden
                      >
                        <path
                          d="M11.3742 0H0V1.99316L11.2938 1.71601C12.3158 1.69133 13.1522 2.51707 13.1311 3.53073L12.8861 15.7934H14.8593V3.4586C14.8593 1.54897 13.2976 0 11.3723 0Z"
                          fill="white"
                        />
                        <path
                          d="M9.37226 4.30513L0 14.3525L1.42584 15.8028L10.8517 5.66047C11.2325 5.25045 11.2019 4.61264 10.7828 4.24059C10.3732 3.87612 9.74546 3.9046 9.37226 4.30513Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-900 truncate">
                        {i18n("membershipCadence.orderLineName")}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {i18n("orderSummary.membershipChargedToday")}
                      </p>
                    </div>
                    <p className="text-sm font-medium text-neutral-900 flex-shrink-0 pl-2">
                      {formatPrice(cadenceCents, market)}/yr
                    </p>
                  </div>
                  {/* The cadence decides what this line costs and how many
                      tests it buys, but until now the only place to change it
                      was the payment step — and on the MRI path the user may
                      never have seen a cadence screen at all. */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCadenceModalOpen(true);
                    }}
                    className="mt-1 text-xs font-normal normal-case text-neutral-500 underline hover:text-neutral-700 transition-colors"
                  >
                    {i18n("paymentInfo.changeCadence")}
                  </button>
                </div>
              )}

              {/* Location. The pencil sits in the header row like every other
                  one-line section — the clinic line itself stays clean. */}
              {selectedClinic && (
                <div className="bg-gray-100 rounded-2xl px-5 py-3">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">
                      {translations.location}
                    </h4>
                    {currentStep !== "clinic" && (
                      <button
                        onClick={() =>
                          goToStep("clinic", { skipValidation: true })
                        }
                        className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200"
                      >
                        <img src={editIcon} alt="Edit" className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {renderClinicInfo()}
                </div>
              )}

              {/* Time Slots. One pencil in the header row — every slot edits
                  the same date step, so per-slot pencils were duplicates. */}
              {selectedTimeSlots && selectedTimeSlots.length > 0 && (
                <div className="bg-gray-100 rounded-2xl px-5 py-3">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">
                      {translations.timeSlots}
                    </h4>
                    {currentStep !== "date" &&
                      currentStep !== "time" &&
                      selectedClinicId && (
                        <button
                          onClick={() =>
                            goToStep("date", { skipValidation: true })
                          }
                          className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200"
                        >
                          <img src={editIcon} alt="Edit" className="h-4 w-4" />
                        </button>
                      )}
                  </div>
                  {renderTimeSlots()}
                </div>
              )}

              {/* Last line of the summary, directly above the pinned total: it
                  sums what every priced line saved, so it reads as a closing
                  statement about the order rather than a note on the products.
                  Between the priced lines and Location it split the two into
                  what looked like unrelated halves. */}
              {isBundleMode && memberSavingsCents > 0 && (
                <p className="flex items-center gap-1.5 px-1 text-sm font-medium text-deep-green-700">
                  <CheckmarkIcon className="size-4 shrink-0" />
                  {i18n("orderSummary.bundleSavings").replace(
                    "{{amount}}",
                    formatPrice(memberSavingsCents, market),
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer - pinned at bottom of the flex column, outside the scroll area */}
        <div className="px-5 pt-6 pb-6 border-t border-gray-200 flex-shrink-0">
          {membershipPlanActive ? null : showMemberPricing ? (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <CheckmarkIcon />
              <span>{i18n("orderSummary.yearlySubscription")}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              {/* Prepay markets (DE/NL) charge up front, so "no upfront
                  payment" would be false — only free cancellation survives. */}
              <CheckmarkIcon />
              <span>
                {i18n(
                  prepayEnabled
                    ? "orderSummary.freeCancellation"
                    : "orderSummary.noUpfrontPayment",
                )}
              </span>
            </div>
          )}
          <div className="mb-4">
            {/* Membership savings breakdown — suppressed in bundle mode (netting shown inline) */}
            {!isBundleMode &&
              (isMember || showMemberPricing) &&
              membershipSavings > 0 && (
                <div className="mb-3 space-y-1.5">
                  <div className="flex items-center justify-between text-sm text-deep-green-600">
                    <span>{i18n("membership.memberDiscount")}</span>
                    <span key={membershipSavings}>
                      -{formatPrice(membershipSavings, market)}
                    </span>
                  </div>
                  {!membershipPlanActive && (
                    <div className="border-t border-gray-200 my-2" />
                  )}
                </div>
              )}
            {/* Coupon discount breakdown */}
            {discount && discount.value > 0 && (
              <div className="mb-3 space-y-1">
                {!isMember && (
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>{i18n("orderSummary.subtotal")}</span>
                    <span>{formatPrice(preDiscountTotal, market)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm text-deep-green-600">
                  <span>
                    {i18n("orderSummary.invitationCode")} ({discount.code})
                  </span>
                  <span data-testid="voucher-discount-amount">
                    -{formatPrice(discountCents, market)}
                  </span>
                </div>
                {/* On a recurring membership, say whether the voucher is a
                    one-off or applies to every renewal. */}
                {showMembership && discount.duration && (
                  <div className="text-xs text-gray-500">
                    {discount.duration === "once"
                      ? i18n("orderSummary.discountRenewsAt", {
                          price: getCadencePriceChf(
                            membershipPlan,
                            membershipTiers,
                          ),
                          defaultValue: "Renews at CHF {{price}}/year",
                        })
                      : i18n("orderSummary.discountEveryRenewal")}
                  </div>
                )}
                <div className="border-t border-gray-200 my-2"></div>
              </div>
            )}
            <div
              key={totalPrice}
              className="flex items-center justify-between"
              style={{ contain: "paint" }}
            >
              <span className="text-lg font-medium text-gray-900">
                {translations.totalPrice}
              </span>
              <span className="flex items-baseline gap-2 whitespace-nowrap">
                {!isBundleMode &&
                  showMemberPricing &&
                  originalTotal > totalPrice && (
                    <span className="text-sm text-gray-400 line-through">
                      {formatPrice(originalTotal, market)}
                    </span>
                  )}
                <span
                  data-testid="order-summary-total"
                  className="text-xl font-bold text-gray-900"
                >
                  {formatPrice(totalPrice, market)}
                </span>
              </span>
            </div>
            {/* Bundle: recurring annotation + savings story below total */}
            {isBundleMode && bundlePricing && (
              <p className="text-sm text-neutral-500 text-right mt-1">
                {i18n("orderSummary.recurringHeading")}:{" "}
                {formatPrice(bundlePricing.recurringCents, market)}/yr
              </p>
            )}
          </div>

          {/* Navigation */}
          <div className="flex gap-2">
            {canGoBack && (
              <button
                type="button"
                onClick={goBack}
                className="bg-neutral-50 hover:bg-neutral-100 text-aubergine-800 rounded-full h-[44px] px-4 font-medium text-base normal-case flex items-center gap-2 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                {translations.back}
              </button>
            )}
            {currentStep === "details" ? (
              <PaymentCtaButton
                cta={paymentCta}
                className={`${canGoBack ? "flex-1 min-w-0" : "w-full"} px-4`}
                submitTestId="desktop-sidebar-submit"
                formId="booking-form"
              />
            ) : (
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canProceed}
                className={`${canGoBack ? "flex-1 min-w-0" : "w-full"} bg-aubergine-800 hover:bg-aubergine-800/90 text-white rounded-full h-[44px] px-4 font-medium text-base normal-case flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:pointer-events-none`}
                data-testid="order-summary-continue"
              >
                {translations.continue}
              </button>
            )}
          </div>
        </div>
      </div>

      <CadencePickerModal
        isOpen={cadenceModalOpen}
        onClose={() => setCadenceModalOpen(false)}
        language={language}
        source="cart"
      />
    </div>
  );
};

export default PersistentDesktopSidebar;
